import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { gnf } from '@/lib/invoices';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/QueryError';

import { OffsetPager } from './OffsetPager';
import { SummaryTile } from './SummaryTile';

import type { FuelTransaction } from '@logistech/ui-shared';

/** GET /fuel/stats?vehicleId= (fuel-service) : seuls les champs lus ici */
export interface VehicleFuelStats {
  totals: { liters: number; cost: number; transactions: number };
  vehicles: { vehicleId: string; distKm: number | null; l100km: number | null }[];
}

/** GET /fuel/transactions (fuel-service) : limit/offset, pas le Paginated<T> page/pageSize (TD-034) */
export interface VehiclePaginatedFuelTransactions {
  data: FuelTransaction[];
  total: number;
  limit: number;
  offset: number;
}

// FuelTransaction.source (fuel-service) : saisie dashboard, app chauffeur, import CSV
export const FUEL_SOURCE_BADGE: Record<string, { label: string; variant: 'secondary' | 'default' | 'warning' }> = {
  manual: { label: 'Manuel', variant: 'secondary' },
  driver_app: { label: 'App chauffeur', variant: 'default' },
  import: { label: 'Import', variant: 'warning' },
};

const PAGE_SIZE = 20;
const fmtLiters = (l: number) => `${l.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} L`;
const fmtDateTime = (iso: string) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

export function VehicleFuelTab({ vehicleId }: { vehicleId: string }) {
  const [offset, setOffset] = useState(0);
  // Fenêtre figée au montage : clé de requête stable
  const [from] = useState(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

  const statsQuery = useQuery({
    queryKey: ['fuel-stats', { vehicleId, from }],
    queryFn: async () =>
      (await api.get<VehicleFuelStats>(`/fuel/stats?${new URLSearchParams({ vehicleId, from }).toString()}`)).data,
  });
  const lastQuery = useQuery({
    queryKey: ['fuel-transactions', { vehicleId, limit: 1 }],
    queryFn: async () => (await api.get<VehiclePaginatedFuelTransactions>(`/fuel/transactions?vehicleId=${vehicleId}&limit=1`)).data,
  });
  const listQuery = useQuery({
    queryKey: ['fuel-transactions', { vehicleId, offset }],
    queryFn: async () => {
      const params = new URLSearchParams({ vehicleId, limit: String(PAGE_SIZE), offset: String(offset) });
      return (await api.get<VehiclePaginatedFuelTransactions>(`/fuel/transactions?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  const stats = statsQuery.data;
  const l100km = stats?.vehicles.find((v) => v.vehicleId === vehicleId)?.l100km ?? null;
  const last = lastQuery.data?.data[0];
  const list = listQuery.data;

  return (
    <div className="space-y-4">
      <QueryError query={statsQuery} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryTile
          label="Consommation moyenne"
          value={l100km != null ? `${l100km.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L/100 km` : '—'}
          sub={l100km != null ? '30 derniers jours' : 'relevés d’odomètre insuffisants sur 30 jours'}
          loading={statsQuery.isLoading}
        />
        <SummaryTile
          label="Coût (30 jours)"
          value={gnf(stats?.totals.cost ?? 0)}
          sub={`${stats?.totals.transactions ?? 0} plein(s), ${fmtLiters(stats?.totals.liters ?? 0)}`}
          loading={statsQuery.isLoading}
        />
        <SummaryTile
          label="Dernière recharge"
          value={last ? new Date(last.transactionDate).toLocaleDateString('fr-FR') : 'Aucune'}
          sub={last ? `${fmtLiters(last.liters)} · ${gnf(last.totalAmount, last.currency)}` : undefined}
          loading={lastQuery.isLoading}
        />
      </div>

      <QueryError query={listQuery} />
      <div className="bg-card rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Litres</TableHead>
              <TableHead className="text-right">Prix/L</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Odomètre</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listQuery.isLoading && Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
            ))}
            {list?.data.map((t) => {
              const source = FUEL_SOURCE_BADGE[t.source];
              return (
                <TableRow key={t.id}>
                  <TableCell>{fmtDateTime(t.transactionDate)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtLiters(t.liters)}</TableCell>
                  <TableCell className="text-right tabular-nums">{gnf(t.pricePerLiter, t.currency)}</TableCell>
                  <TableCell className="text-right tabular-nums">{gnf(t.totalAmount, t.currency)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {t.odometerKm != null ? `${t.odometerKm.toLocaleString('fr-FR')} km` : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={source?.variant ?? 'secondary'}>{source?.label ?? t.source}</Badge>
                  </TableCell>
                </TableRow>
              );
            })}
            {list && list.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucun plein enregistré pour ce véhicule</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {list && <OffsetPager total={list.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />}
      </div>
    </div>
  );
}
